import { useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/api/auth'
import { apiFetch, ApiError } from '@/api/client'
import { cn } from '@/lib/utils'

type Resolved = { artikel: { sku: string } }

/**
 * Plan 2026-10-23 S2: a *Sternnummer / PL code that opens its Grundartikel.
 * Resolves only on click (GET /stock/grundartikel/resolve/), so a long list renders without
 * N requests. Ctrl/Meta/Shift or middle click opens a new tab after resolving. The resolver
 * needs the same article read rights as the catalogue destination; otherwise plain text.
 */
export function GrundartikelLink({
  code,
  children,
  className,
  state,
}: {
  code: string | null | undefined
  children?: ReactNode
  className?: string
  state?: unknown
}) {
  const { user } = useAuth()
  const term = code?.trim() ?? ''
  const label = children ?? (term || '—')
  if (!term || !user?.permissions?.article_general) return <span className={className}>{label}</span>
  return <ResolvingLink term={term} className={className} state={state}>{label}</ResolvingLink>
}

/** Router/query hooks only where a link is actually shown. */
function ResolvingLink({ term, children, className, state }: { term: string; children: ReactNode; className?: string; state?: unknown }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function open(newTab: boolean) {
    if (busy) return
    // Open the tab synchronously inside the user gesture; a later window.open is blocked.
    const tab = newTab ? window.open('', '_blank') : null
    setBusy(true)
    setError('')
    try {
      const r = await qc.fetchQuery({
        queryKey: ['stock', 'grundartikel', 'resolve', term],
        queryFn: () => apiFetch<Resolved>(`/stock/grundartikel/resolve/?q=${encodeURIComponent(term)}`),
        staleTime: 5 * 60_000,
      })
      const path = `/catalog/${encodeURIComponent(r.artikel.sku)}`
      if (tab) {
        tab.opener = null
        tab.location.href = path
      } else if (newTab) window.open(path, '_blank', 'noopener')
      else navigate(path, { state })
    } catch (e) {
      tab?.close()
      setError(e instanceof ApiError && e.status === 404
        ? `Kein Grundartikel zu ${term}`
        : 'Grundartikel konnte nicht geöffnet werden.')
    } finally {
      setBusy(false)
    }
  }
  function onClick(e: MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    void open(e.ctrlKey || e.metaKey || e.shiftKey)
  }
  function onAuxClick(e: MouseEvent) {
    if (e.button !== 1) return
    e.preventDefault()
    e.stopPropagation()
    void open(true)
  }
  function onKeyDown(e: KeyboardEvent) {
    if (e.key !== 'Enter') return
    e.preventDefault()
    e.stopPropagation()
    void open(e.ctrlKey || e.metaKey)
  }
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
      <a
        role="link"
        tabIndex={0}
        title="Grundartikel öffnen"
        aria-busy={busy || undefined}
        className={cn(
          'cursor-pointer text-primary underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none',
          busy && 'cursor-progress opacity-70',
          className,
        )}
        onClick={onClick}
        onAuxClick={onAuxClick}
        onMouseDown={(e) => { if (e.button === 1) e.preventDefault() }}
        onKeyDown={onKeyDown}
      >
        {children}
      </a>
      {error && <span role="alert" className="text-xs text-red-600">{error}</span>}
    </span>
  )
}
